import { useMemo, useRef, useState, useEffect } from 'react';
import { scaleBand, scaleLinear } from 'd3-scale';
import { useThemeColors } from './useThemeColors.js';

/**
 * Barres groupées capacité vs demande (allocated) — SVG maison (d3-scale).
 * Couleurs via le thème. data-testid sur chaque rect.
 *
 * data : [{ period, capacityHours, allocatedHours }]
 */
export default function BarChart({ data = [], height = 280, formatLabel }) {
  const colors = useThemeColors();
  const ref = useRef(null);
  const [width, setWidth] = useState(640);
  const svgRef = useRef(null);

  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(Math.max(240, Math.round(w)));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const margin = { top: 12, right: 12, bottom: 36, left: 44 };
  const innerW = Math.max(1, width - margin.left - margin.right);
  const innerH = Math.max(1, height - margin.top - margin.bottom);

  const { x, xSub, y, ticks } = useMemo(() => {
    const x0 = scaleBand()
      .domain(data.map((d) => d.period))
      .range([0, innerW])
      .padding(0.2);
    const x1 = scaleBand()
      .domain(['capacityHours', 'allocatedHours'])
      .range([0, x0.bandwidth()])
      .padding(0.1);
    const maxV = Math.max(
      1,
      ...data.map((d) => Math.max(d.capacityHours || 0, d.allocatedHours || 0)),
    );
    const yS = scaleLinear().domain([0, maxV]).nice().range([innerH, 0]);
    return { x: x0, xSub: x1, y: yS, ticks: yS.ticks(4) };
  }, [data, innerW, innerH]);

  const seriesColor = { capacityHours: colors.glassBorder, allocatedHours: colors.accent };

  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        role="img"
        data-testid="bar-chart"
        style={{ display: 'block' }}
      >
        <g transform={`translate(${margin.left},${margin.top})`}>
          {ticks.map((t) => (
            <g key={t} transform={`translate(0,${y(t)})`}>
              <line x1={0} x2={innerW} stroke={colors.glassBorder} strokeDasharray="2 3" />
              <text x={-8} dy="0.32em" textAnchor="end" fontSize={10} fill={colors.textMuted}>
                {t}
              </text>
            </g>
          ))}
          {data.map((d) => (
            <g key={d.period} transform={`translate(${x(d.period)},0)`}>
              {['capacityHours', 'allocatedHours'].map((k) => {
                const v = d[k] || 0;
                return (
                  <rect
                    key={k}
                    data-testid={`bar-${d.period}-${k}`}
                    x={xSub(k)}
                    y={y(v)}
                    width={xSub.bandwidth()}
                    height={Math.max(0, innerH - y(v))}
                    fill={seriesColor[k]}
                    rx={2}
                  >
                    <title>{`${formatLabel ? formatLabel(d.period) : d.period} — ${k === 'capacityHours' ? 'Capacité' : 'Demande'} : ${v}h`}</title>
                  </rect>
                );
              })}
            </g>
          ))}
          <line x1={0} y1={innerH} x2={innerW} y2={innerH} stroke={colors.glassBorder} />
          {data.map((d) => (
            <text
              key={d.period}
              x={x(d.period) + x.bandwidth() / 2}
              y={innerH + 14}
              textAnchor="middle"
              fontSize={9}
              fill={colors.textMuted}
            >
              {formatLabel ? formatLabel(d.period) : d.period}
            </text>
          ))}
        </g>
      </svg>
    </div>
  );
}
